/**
 * The playback track, in the three shapes a track is ever drawn in, and the
 * component that ends the native range input in this system.
 *
 * `<input type="range">` cannot be told what a position MEANS. It reads out as
 * a bare number, so "1:04 of 0:30" arrives at a screen reader as "sixty-four",
 * and no amount of styling makes its thumb match a card. So the slider is built
 * here: `role="slider"` with `aria-valuetext` in human form from
 * `formatPlaybackPosition`, arrow / Home / End keys, and a pointer drag held by
 * pointer capture rather than by window listeners — capture keeps the gesture
 * on this element when the pointer leaves it, which is exactly what a global
 * `pointermove` listener would otherwise be armed for.
 *
 * Seekability is a capability, not a style: `onSeek === undefined` means the
 * track is a `role="progressbar"` and takes no focus. A focusable control that
 * does nothing is worse than no control.
 *
 * In `Bed` and `Ring` the content is a SIBLING of the role element, never its
 * child. `progressbar` and `slider` both take presentational children, so a
 * row's title and artist — or the artwork a `Ring` is drawn around — would
 * leave the accessibility tree entirely, and a lightbox trigger would be a
 * button nested inside a slider. The role element is a layer instead: it holds
 * the semantics and, when the track is seekable, the gesture.
 *
 * The played fill is `transform: scaleX(fraction)` with `transition: none`
 * while sound is moving, on purpose — a fill that eases toward the clock is a
 * fill that disagrees with the clock, and the reader can see both at once. The
 * ONE exception is a fraction returning to zero, which is not the clock moving
 * but the track being given up; that empties over the reveal clock, because a
 * fill that vanishes between two frames reads as a rendering fault.
 */
"use client";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type AriaRole,
  type RefObject,
  type CSSProperties,
  type KeyboardEvent,
  type KeyboardEventHandler,
  type PointerEvent,
  type PointerEventHandler,
  type ReactElement,
  type ReactNode,
} from "react";
import { formatPlaybackPosition } from "./cresultcard.format";
import {
  crcTransition,
  CrcAnimatedProperty,
  CrcDuration,
  CrcEase,
} from "./cresultcard.motion";
import { useCresultcardNestedRadius } from "./cresultcard.radius";
import {
  CRC_HAIRLINE,
  CRC_TONE_COLOR,
  CRC_TOUCH_TARGET,
} from "./cresultcard.tokens";
import {
  CresultcardProgressGeometry,
  CresultcardTone,
} from "./cresultcard.types";
import { useCresultcardViewport } from "./cresultcard.viewport";
import "./Cresultcard.css";

const BUFFER_SWEEP_CLASS_NAME = "cresultcard__buffer-sweep";

const SLIDER_ROLE = "slider";
const PROGRESSBAR_ROLE = "progressbar";
const DIRECTION_RTL = "rtl";
/** A drag must not also scroll the page it is dragging over. */
const TOUCH_ACTION_NONE = "none";
const CURSOR_POINTER = "pointer";
const CURSOR_DEFAULT = "default";
/**
 * The fill's growth edge is the INLINE start, and there is no logical keyword
 * for `transform-origin` — so it comes from the stylesheet, which states both
 * directions. An inline `left center` on a component that carefully reads
 * `direction` for its own scrubbing was a fill growing from the wrong end of a
 * right-to-left line.
 */
const FILL_ORIGIN_CLASS_NAME = "cresultcard__fill-origin";
const NO_TRANSITION = "none";
const FULL_WIDTH = "100%";
const FULL_HEIGHT = "100%";
const RING_ROTATION = "rotate(-90deg)";
const RING_ORIGIN = "center";
const SVG_NO_FILL = "none";
const SWEEP_WIDTH = "26%";
const SWEEP_GRADIENT =
  "linear-gradient(90deg, rgba(102,254,193,0), rgba(102,254,193,0.55), rgba(102,254,193,0))";

const LINE_THICKNESS = 3;
const LINE_THICKNESS_MOBILE = 4;
/** A 3px line is not a target. The hit box grows; the paint does not. */
const HIT_HEIGHT_WIDE = 18;

/** One viewBox, so a ring is correct at a 28px thumb and at a 120px hero. */
const RING_VIEWBOX = "0 0 100 100";
const RING_CENTRE = 50;
const RING_RADIUS = 46;
const RING_STROKE = 5;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const RING_LINECAP = "round";
/**
 * The scrub gesture belongs to the BAND, not to the disc inside it.
 *
 * A seekable ring used to take the pointer across its whole square, so while a
 * track was playing the artwork it is drawn around stopped being a viewer
 * trigger — a click on the sleeve scrubbed to eight seconds instead of opening
 * the picture. The hit area is an unpainted stroke on the band instead: SVG's
 * `pointer-events: stroke` answers over the stroke area whether or not a stroke
 * is painted, which is exactly an annulus, and the centre stays with whatever
 * the ring surrounds. The holder itself takes no pointer, so the gesture
 * arrives here by bubbling and `currentTarget` is still the holder the angle
 * maths measures from.
 */
const RING_HIT_RADIUS = 48;
const RING_HIT_STROKE = 14;
const RING_HIT_POINTER_EVENTS = "stroke";
const POINTER_EVENTS_NONE = "none";

/** ArrowLeft / ArrowRight move by 2% — about half a second of a 30s preview. */
const SEEK_STEP = 0.02;

const FRACTION_MIN = 0;
const FRACTION_MAX = 1;

/** The played fill sits behind content in `Bed`, so it is a tint, not a bar. */
const BED_FILL_OPACITY = 0.16;
/** Buffered is "we have it", not "you have heard it". */
const BUFFERED_OPACITY = 0.24;

/** The one eased state: a track being given up, rather than a clock advancing. */
const EMPTYING_FILL_TRANSITION = crcTransition({
  properties: [CrcAnimatedProperty.Transform],
  duration: CrcDuration.Reveal,
  ease: CrcEase.Settle,
});
const EMPTYING_ARC_TRANSITION = crcTransition({
  properties: [CrcAnimatedProperty.StrokeDashoffset],
  duration: CrcDuration.Reveal,
  ease: CrcEase.Settle,
});

enum TrackKey {
  ArrowLeft = "ArrowLeft",
  ArrowRight = "ArrowRight",
  ArrowUp = "ArrowUp",
  ArrowDown = "ArrowDown",
  Home = "Home",
  End = "End",
}

/**
 * The semantics all three geometries share. It is a declared type rather than
 * an inferred literal because `role` has to stay `AriaRole` through the spread:
 * an inferred object literal widens it to `string` and the div stops accepting
 * it.
 */
interface TrackHolderAttributes {
  readonly "aria-label": string;
  readonly "aria-valuemax": number;
  readonly "aria-valuemin": number;
  readonly "aria-valuenow": number;
  readonly "aria-valuetext": string;
  readonly role: AriaRole;
  readonly tabIndex: number | undefined;
  readonly onKeyDown: KeyboardEventHandler<HTMLDivElement> | undefined;
  readonly onPointerCancel: PointerEventHandler<HTMLDivElement> | undefined;
  readonly onPointerDown: PointerEventHandler<HTMLDivElement> | undefined;
  readonly onPointerMove: PointerEventHandler<HTMLDivElement> | undefined;
  readonly onPointerUp: PointerEventHandler<HTMLDivElement> | undefined;
}

function clampFraction(fraction: number): number {
  if (!Number.isFinite(fraction)) {
    return FRACTION_MIN;
  }
  return Math.min(Math.max(fraction, FRACTION_MIN), FRACTION_MAX);
}

/** The two shapes a played fraction is drawn as. Both are module-level so the
 *  writer handed to the fill hook is stable across renders. */
type CresultcardFillElement = HTMLElement | SVGElement;

function writeScaleXFill(
  element: CresultcardFillElement,
  fraction: number,
): void {
  element.style.transform = `scaleX(${String(fraction)})`;
}

function writeDashoffsetFill(
  element: CresultcardFillElement,
  fraction: number,
): void {
  element.style.strokeDashoffset = String(
    RING_CIRCUMFERENCE * (FRACTION_MAX - fraction),
  );
}

interface UseEasedFillInput {
  readonly fraction: number;
  readonly transition: string;
  readonly write: (element: CresultcardFillElement, fraction: number) => void;
}

/**
 * The played fill, written imperatively so its transition can be set BEFORE
 * the value it applies to. The fill tracks the media clock 1:1 with no
 * transition while sound is moving; the one exception is a fraction returning
 * to zero, which is the track being given up rather than the clock moving, and
 * which snaps out of existence between two frames if it is not eased.
 */
function useEasedFill<TElement extends CresultcardFillElement>({
  fraction,
  transition,
  write,
}: UseEasedFillInput): RefObject<TElement | null> {
  const nodeRef = useRef<TElement | null>(null);
  const previousRef = useRef<number>(fraction);

  useLayoutEffect((): void => {
    const previous = previousRef.current;
    previousRef.current = fraction;
    const node = nodeRef.current;
    if (node == null) {
      return;
    }
    node.style.transition =
      fraction === FRACTION_MIN && previous > FRACTION_MIN
        ? transition
        : NO_TRANSITION;
    write(node, fraction);
  }, [fraction, transition, write]);

  return nodeRef;
}

/**
 * Direction is read from the element rather than assumed, so a right-to-left
 * record's track fills and scrubs from the same edge its text starts on.
 */
function isRightToLeft(element: HTMLElement): boolean {
  return window.getComputedStyle(element).direction === DIRECTION_RTL;
}

/**
 * A capture belongs to the element the browser actually hit. The ring's gesture
 * starts on its band, and the holder that carries the handlers takes no pointer
 * of its own, so capturing on `currentTarget` would be capturing on a layer the
 * pointer never touched.
 */
function pointerCaptureTarget(event: PointerEvent<HTMLDivElement>): Element {
  return event.target instanceof Element ? event.target : event.currentTarget;
}

export interface CresultcardProgressTrackProps {
  readonly geometry: CresultcardProgressGeometry;
  /** 0…1. Clamped here so a caller's division by zero cannot draw past the end. */
  readonly fraction: number;
  /** 0…1 of the media that has arrived, drawn behind the played fill. */
  readonly buffered?: number;
  /** `undefined` means NOT seekable: the track becomes a plain progress indicator. */
  readonly onSeek?: (fraction: number) => void;
  readonly elapsed: number;
  readonly total: number;
  readonly label: string;
  /**
   * Not in the primitive's headline prop list, and needed: the empty track is
   * where waiting is shown, and the track cannot infer it from a fraction that
   * is legitimately zero at the start of every item.
   */
  readonly buffering?: boolean;
  readonly tone?: CresultcardTone;
  /**
   * The content the track is drawn around (`Ring`) or behind (`Bed`). It is a
   * sibling of the role element, so it keeps its own place in the
   * accessibility tree and may contain controls of its own.
   */
  readonly children?: ReactNode;
}

export function CresultcardProgressTrack({
  geometry,
  fraction,
  buffered,
  onSeek,
  elapsed,
  total,
  label,
  buffering = false,
  tone = CresultcardTone.Positive,
  children,
}: CresultcardProgressTrackProps): ReactElement {
  const { isMobile } = useCresultcardViewport();
  const bedRadius = useCresultcardNestedRadius();
  const [dragging, setDragging] = useState<boolean>(false);

  const seekable = onSeek != null;
  const played = clampFraction(fraction);
  const ahead = buffered == null ? FRACTION_MIN : clampFraction(buffered);
  const barFillRef = useEasedFill<HTMLSpanElement>({
    fraction: played,
    transition: EMPTYING_FILL_TRANSITION,
    write: writeScaleXFill,
  });
  const arcFillRef = useEasedFill<SVGCircleElement>({
    fraction: played,
    transition: EMPTYING_ARC_TRANSITION,
    write: writeDashoffsetFill,
  });
  const fillColor = CRC_TONE_COLOR[tone];
  const thickness = isMobile ? LINE_THICKNESS_MOBILE : LINE_THICKNESS;
  const hitHeight = isMobile ? CRC_TOUCH_TARGET : HIT_HEIGHT_WIDE;

  const hasDuration = Number.isFinite(total) && total > 0;
  const valueMax = hasDuration ? total : FRACTION_MIN;
  const valueNow = hasDuration
    ? Math.min(Math.max(elapsed, FRACTION_MIN), total)
    : FRACTION_MIN;
  const valueText = formatPlaybackPosition({
    elapsedSeconds: elapsed,
    totalSeconds: total,
  });

  const commitSeek = useCallback(
    (next: number): void => {
      onSeek?.(clampFraction(next));
    },
    [onSeek],
  );

  /**
   * The ring is scrubbed by angle from its centre and the two straight
   * geometries by inline offset. Both read the element's own direction, so
   * neither hard-codes a physical edge.
   */
  const isRing = geometry === CresultcardProgressGeometry.Ring;
  const pointerFraction = useCallback(
    (event: PointerEvent<HTMLDivElement>): number => {
      const element = event.currentTarget;
      const rect = element.getBoundingClientRect();
      if (isRing) {
        const dx = event.clientX - (rect.left + rect.width / 2);
        const dy = event.clientY - (rect.top + rect.height / 2);
        const turn = Math.PI * 2;
        const angle = Math.atan2(dy, dx) + Math.PI / 2;
        return (((angle % turn) + turn) % turn) / turn;
      }
      if (rect.width <= 0) {
        return FRACTION_MIN;
      }
      const offset = isRightToLeft(element)
        ? rect.right - event.clientX
        : event.clientX - rect.left;
      return offset / rect.width;
    },
    [isRing],
  );

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>): void => {
      if (!seekable) {
        return;
      }
      pointerCaptureTarget(event).setPointerCapture(event.pointerId);
      setDragging(true);
      commitSeek(pointerFraction(event));
    },
    [commitSeek, pointerFraction, seekable],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>): void => {
      if (!dragging) {
        return;
      }
      commitSeek(pointerFraction(event));
    },
    [commitSeek, dragging, pointerFraction],
  );

  const handlePointerRelease = useCallback(
    (event: PointerEvent<HTMLDivElement>): void => {
      if (!dragging) {
        return;
      }
      const element = pointerCaptureTarget(event);
      if (element.hasPointerCapture(event.pointerId)) {
        element.releasePointerCapture(event.pointerId);
      }
      setDragging(false);
    },
    [dragging],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (!seekable) {
        return;
      }
      const rtl = isRightToLeft(event.currentTarget);
      const forwardKey = rtl ? TrackKey.ArrowLeft : TrackKey.ArrowRight;
      const backwardKey = rtl ? TrackKey.ArrowRight : TrackKey.ArrowLeft;
      const key = event.key;
      if (key === forwardKey || key === TrackKey.ArrowUp) {
        event.preventDefault();
        commitSeek(played + SEEK_STEP);
        return;
      }
      if (key === backwardKey || key === TrackKey.ArrowDown) {
        event.preventDefault();
        commitSeek(played - SEEK_STEP);
        return;
      }
      if (key === TrackKey.Home) {
        event.preventDefault();
        commitSeek(FRACTION_MIN);
        return;
      }
      if (key === TrackKey.End) {
        event.preventDefault();
        commitSeek(FRACTION_MAX);
      }
    },
    [commitSeek, played, seekable],
  );

  /** One attribute bag, so the three geometries cannot drift on semantics. */
  const holderAttributes: TrackHolderAttributes = {
    "aria-label": label,
    "aria-valuemax": valueMax,
    "aria-valuemin": FRACTION_MIN,
    "aria-valuenow": valueNow,
    "aria-valuetext": valueText,
    role: seekable ? SLIDER_ROLE : PROGRESSBAR_ROLE,
    tabIndex: seekable ? 0 : undefined,
    onKeyDown: seekable ? handleKeyDown : undefined,
    onPointerCancel: seekable ? handlePointerRelease : undefined,
    onPointerDown: seekable ? handlePointerDown : undefined,
    onPointerMove: seekable ? handlePointerMove : undefined,
    onPointerUp: seekable ? handlePointerRelease : undefined,
  };
  /** Stamped separately: it is how the kit's quiet click zone spares a control. */
  const controlMarker = seekable ? true : undefined;
  const interactionStyle: CSSProperties = {
    cursor: seekable ? CURSOR_POINTER : CURSOR_DEFAULT,
    touchAction: seekable ? TOUCH_ACTION_NONE : undefined,
  };

  const sweep = buffering ? (
    <span
      aria-hidden
      className={BUFFER_SWEEP_CLASS_NAME}
      style={{
        position: "absolute",
        insetBlock: 0,
        insetInlineStart: 0,
        width: SWEEP_WIDTH,
        borderRadius: thickness,
        backgroundImage: SWEEP_GRADIENT,
      }}
    />
  ) : null;

  if (isRing) {
    return (
      <div
        data-el="Cresultcard/ProgressTrack/Ring"
        style={{
          position: "relative",
          display: "inline-flex",
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        {children}
        <div
          {...holderAttributes}
          data-crc-control={controlMarker}
          style={{
            ...interactionStyle,
            position: "absolute",
            inset: 0,
            // The holder never takes the pointer itself. A ring is drawn AROUND
            // something — artwork that is its own viewer trigger — and a layer
            // covering that centre is a control eating the control beneath it.
            // The band below carries the gesture and bubbles it up to here.
            pointerEvents: POINTER_EVENTS_NONE,
          }}
        >
          <svg
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              width: FULL_WIDTH,
              height: FULL_HEIGHT,
              transform: RING_ROTATION,
              transformOrigin: RING_ORIGIN,
              pointerEvents: POINTER_EVENTS_NONE,
            }}
            viewBox={RING_VIEWBOX}
          >
            {seekable && (
              <circle
                cx={RING_CENTRE}
                cy={RING_CENTRE}
                fill={SVG_NO_FILL}
                r={RING_HIT_RADIUS}
                stroke={SVG_NO_FILL}
                strokeWidth={RING_HIT_STROKE}
                style={{
                  ...interactionStyle,
                  pointerEvents: RING_HIT_POINTER_EVENTS,
                }}
              />
            )}
            <circle
              cx={RING_CENTRE}
              cy={RING_CENTRE}
              fill={SVG_NO_FILL}
              r={RING_RADIUS}
              stroke={CRC_HAIRLINE}
              strokeWidth={RING_STROKE}
            />
            {ahead > FRACTION_MIN && (
              <circle
                cx={RING_CENTRE}
                cy={RING_CENTRE}
                fill={SVG_NO_FILL}
                opacity={BUFFERED_OPACITY}
                r={RING_RADIUS}
                stroke={fillColor}
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={RING_CIRCUMFERENCE * (FRACTION_MAX - ahead)}
                strokeLinecap={RING_LINECAP}
                strokeWidth={RING_STROKE}
                style={{ transition: NO_TRANSITION }}
              />
            )}
            <circle
              cx={RING_CENTRE}
              cy={RING_CENTRE}
              fill={SVG_NO_FILL}
              r={RING_RADIUS}
              ref={arcFillRef}
              stroke={fillColor}
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeLinecap={RING_LINECAP}
              strokeWidth={RING_STROKE}
            />
          </svg>
        </div>
      </div>
    );
  }

  if (geometry === CresultcardProgressGeometry.Bed) {
    return (
      <div
        data-el="Cresultcard/ProgressTrack/Bed"
        style={{
          position: "relative",
          minWidth: 0,
          overflow: "hidden",
          borderRadius: bedRadius,
        }}
      >
        <span
          aria-hidden
          className={FILL_ORIGIN_CLASS_NAME}
          ref={barFillRef}
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: fillColor,
            opacity: BED_FILL_OPACITY,
          }}
        />
        {sweep}
        <div
          style={{ position: "relative", minWidth: 0 }}
        >
          {children}
        </div>
        <div
          {...holderAttributes}
          data-crc-control={controlMarker}
          style={{
            ...interactionStyle,
            position: "absolute",
            inset: 0,
            // A bed that cannot be scrubbed must not stand between the reader
            // and the row it is painted behind.
            pointerEvents: seekable ? "auto" : "none",
          }}
        />
      </div>
    );
  }

  return (
    <div
      {...holderAttributes}
      data-el="Cresultcard/ProgressTrack/Track"
      data-crc-control={controlMarker}
      style={{
        ...interactionStyle,
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: FULL_WIDTH,
        minWidth: 0,
        height: seekable ? hitHeight : thickness,
      }}
    >
      <span
        aria-hidden
        style={{
          position: "relative",
          display: "block",
          width: FULL_WIDTH,
          height: thickness,
          overflow: "hidden",
          borderRadius: thickness,
          backgroundColor: CRC_HAIRLINE,
        }}
      >
        {ahead > FRACTION_MIN && (
          <span
            className={FILL_ORIGIN_CLASS_NAME}
            style={{
              position: "absolute",
              insetBlock: 0,
              insetInlineStart: 0,
              width: FULL_WIDTH,
              transform: `scaleX(${String(ahead)})`,
              transition: NO_TRANSITION,
              backgroundColor: fillColor,
              opacity: BUFFERED_OPACITY,
            }}
          />
        )}
        {sweep}
        <span
          className={FILL_ORIGIN_CLASS_NAME}
          ref={barFillRef}
          style={{
            position: "absolute",
            insetBlock: 0,
            insetInlineStart: 0,
            width: FULL_WIDTH,
            backgroundColor: fillColor,
          }}
        />
      </span>
    </div>
  );
}
